import { IOperation } from 'Models/Operation';
import React, { useEffect, useState } from 'react';
import { useGetAllDocumentTypesQuery } from 'redux/api/documentTypeApi';
import { usePostOperationMutation, usePutOperationMutation } from 'redux/api/operationApi';
import { setCurrentOperation, setDocumentTypeName } from 'redux/features/operationSlice';
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { setDocumentTypes } from 'redux/features/documentTypeSlice';
import { set } from 'lodash';
import { useIntl } from 'react-intl';

const OperationDischarge: React.FC<{ nextStep: (stepIncrement?: number) => void }> = ({ nextStep }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [amount, setAmount] = useState(0);
  const [operationDate, setOperationDate] = useState(new Date().toISOString().split('T')[0]);
  const [documentType, setDocumentType] = useState('');
  const [isOperationSubmitted, setIsOperationSubmitted] = useState(false);
  const [error, setError] = useState(false);


  //api
  const [createOperation, { data: postedOperationData, isSuccess: isPostedOperationSuccess, error: apiError }] = usePostOperationMutation();
  const [updateOperation, { data: postedUpdateOperationData, isSuccess: isPostedUpdateOperationSuccess, error: apiUpdateError }] = usePutOperationMutation()

  const { data: documentTypes, isLoading: isDocumentTypesLoading, isError } = useGetAllDocumentTypesQuery();
  const operation = useSelector((state: any) => {
    return state.operationState?.currentOperation?.data ? state.operationState?.currentOperation?.data : state.operationState?.currentOperation;
  });
  useEffect(() => {
    if (operation) {
      setAmount(operation.amount);
      setOperationDate(new Date(operation.operationDate).toISOString().split('T')[0]);
      setDocumentType(operation.documentTypeId);
      const selectedType = documentTypes?.find((type) => type._id === operation.documentTypeId);
      dispatch(setDocumentTypeName(selectedType?.name || '')); // Actualiza el global state
    }
  }, []);
  const handleNextStep = () => {
    const selectedDocumentType = documentTypes?.find((type) => type._id === documentType);
    if (selectedDocumentType?.name === "Passport") {
      nextStep(2);
    } else {
      nextStep();
    }
  }

  const handleDocumentTypeChange = (e: SelectChangeEvent<string>) => {
    const selectedType = documentTypes?.find((type) => type._id === e.target.value);
    setDocumentType(e.target.value as string);
    dispatch(setDocumentTypeName(selectedType?.name || '')); // Actualiza el global state
  };

  const handleUpdateOperation = () => {
    const formData = new FormData();
    const payload = {
      ...operation,
      amount,
      operationDate: new Date(operationDate),
      documentTypeId: documentType
    };
    formData.append("data", JSON.stringify(payload));
    updateOperation({ id: operation.id, operationToUpdate: formData })
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (operationDate === '' || documentType === '') {
      setError(true);
      return;
    }
    setError(false);
    if (isOperationSubmitted) return;
    if (operation && operation._id) {
      handleUpdateOperation();
      return
    }
    setIsOperationSubmitted(true);
    const formData = new FormData();
    const payload = {
      amount,
      operationDate: new Date(operationDate),
      documentTypeId: documentType
    };
    formData.append("data", JSON.stringify(payload));

    createOperation(formData)
  };
  if (isPostedOperationSuccess && postedOperationData) {
    // Verifica si `postedOperationData` tiene la propiedad `data`
    const operationData = "data" in postedOperationData
      ? postedOperationData.data // Si tiene `data`, accede a ella
      : postedOperationData; // Si no, usa el objeto directamente

    dispatch(setCurrentOperation(operationData));

    handleNextStep();
  }
  if (isPostedUpdateOperationSuccess && postedUpdateOperationData) {
    const operationData = "data" in postedUpdateOperationData
      ? postedUpdateOperationData.data // Si tiene `data`, accede a ella
      : postedUpdateOperationData; // Si no, usa el objeto directamente

    dispatch(setCurrentOperation(operationData));
    handleNextStep();
  }
  if (apiError) {
    console.log('Error:', apiError);
    return <div>{JSON.stringify(apiError)}</div>;
  }

  return (
    <Card sx={{ p: 2 }}>
      <CardHeader
        title={
          <Typography variant="h1" align="center">
           {formatMessage({ id: "operation.discharge.title" })}
          </Typography>
        }
      />
      <CardContent>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <TextField
              type="number"
              id="amount"
              value={amount > 0 ? amount : ''}
              onChange={(e) => setAmount(Number(e.target.value))}
              fullWidth
              label={formatMessage({ id: "operation.discharge.amount" })}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <TextField
              type="date"
              id="operationDate"
              value={operationDate}
              onChange={(e) => setOperationDate(e.target.value)}
              fullWidth
              label={formatMessage({ id: "operation.discharge.date" })}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel id="documentType-label">{formatMessage({ id: "operation.discharge.doctype" })}</InputLabel>
              <Select
                id="documentType"
                value={documentType}
                onChange={handleDocumentTypeChange}
                label={formatMessage({ id: "operation.discharge.doctype" })}
                disabled={!!operation}
              >
                <MenuItem value="" disabled>
                  {isDocumentTypesLoading
                    ? formatMessage({ id: "operation.discharge.loading" })
                    : formatMessage({ id: "operation.discharge.select.type" })}
                </MenuItem>
                {documentTypes?.map((type) => (
                  <MenuItem key={type._id} value={type._id}>
                    {formatMessage({ id: `documentType.${type.name}` })}
                  </MenuItem>
                ))}
                {isError && (
                  <MenuItem value="">
                    {formatMessage({ id: "operation.discharge.options.error" })}                    
                  </MenuItem>
                )}
              </Select>
              {!!operation && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {formatMessage({ id: "operation.discharge.change.error" })}                   
                </Typography>
              )}
            </FormControl>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            fullWidth
            size="large"
          >
            <Typography fontWeight={500} fontSize="16px" color="#FFF">
            {formatMessage({ id: "operation.discharge.begin" })}
            </Typography>
          </Button>
          {error && (
            <Typography color="error" sx={{ mt: 2 }} align="center">
              {formatMessage({ id: "operation.discharge.data.error" })}
            </Typography>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default OperationDischarge;
